import {
  $getFiltersRequestType,
  $getPaginationResponseType,
} from '@shared/types';
import { z } from 'zod';

export type UserVacancy = z.infer<typeof $UserVacancy>;
export const $UserVacancy = z.object({
  id: z.number(),
  name: z.string(),
  cityIds: z.array(z.number()).optional(),
  cityId: z.number().optional(),
  description: z.string(),
  salaryFrom: z.number(),
  salaryTo: z.number(),
  mandatoryRequirements: z.string(),
  additionalRequirements: z.string(),
  responsibilities: z.string(),
  isActive: z.boolean(),
  isArchived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  paymentPeriod: z.object({
    id: z.number(),
    name: z.string(),
  }),
  vacancyActivities: z.object({
    id: z.number(),
    vacancyId: z.number(),
    activeFrom: z.string(),
    activeTo: z.string(),
  }),
  currency: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    value: z.string(),
  }),
  company: z.object({
    id: z.number(),
    name: z.string(),
    type: z.string(),
    nif: z.string(),
    regionId: z.number(),
    description: z.string(),
    isActive: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
    manager: z.object({
      id: z.number(),
      email: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      vacanciesCount: z.number(),
      role: z.object({
        id: z.number(),
        name: z.string(),
        displayName: z.string(),
        permissions: z.array(z.string()),
      }),
      profile: z.object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        phone: z.string(),
        position: z.string(),
      }),
      company: z.string(),
      vacancies: z.string(),
    }),
    region: z.object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      countryCode: z.string(),
    }),
    activitySpheres: z.object({
      id: z.number(),
      name: z.string(),
    }),
  }),
  cities: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        countryCode: z.string(),
      })
    )
    .optional(),
  city: z
    .object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      countryCode: z.string(),
    })
    .optional(),
  responsibleUser: z.object({
    id: z.number(),
    email: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    vacanciesCount: z.number(),
    role: z.object({
      id: z.number(),
      name: z.string(),
      displayName: z.string(),
      permissions: z.array(z.string()),
    }),
    profile: z.object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      phone: z.string(),
      position: z.string(),
    }),
    company: z.string(),
    vacancies: z.string(),
  }),
  employmentTypes: z.object({
    id: z.number(),
    name: z.string(),
  }),
  scheduleWorks: z.object({
    id: z.number(),
    name: z.string(),
  }),
  skills: z.object({
    id: z.number(),
    name: z.string(),
  }),
  paymentPeriods: z.object({
    id: z.number(),
    name: z.string(),
  }),
});

export type User = z.infer<typeof $User>;
export const $User = z.object({
  id: z.number(),
  email: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  role: z.object({
    id: z.number(),
    name: z.string(),
    displayName: z.string(),
  }),
  profile: z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string(),
    position: z.string(),
    avatar: z.object({
      id: z.number(),
      absolutePath: z.string(),
    }),
  }),
  company: z.object({
    id: z.number(),
    name: z.string(),
    type: z.string(),
    nif: z.string(),
    regionId: z.number(),
    description: z.string(),
    isActive: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
    region: z.object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      countryCode: z.string(),
    }),
  }),
  isActive: z.boolean(),
  vacancies: z.array($UserVacancy),
});

export type Users = z.infer<typeof $Users>;
export const $Users = z.array($User);

export type GetUsersResponse = z.infer<typeof $GetUsersResponse>;
export const $GetUsersResponse = z.array($User);

export const $GetUsersRequest = $getFiltersRequestType(z.object({}));
export type GetUsersRequest = z.infer<typeof $GetUsersRequest>;

export type UpdateUserRequest = z.infer<typeof $UpdateUserRequest>;
export const $UpdateUserRequest = z.object({
  email: z.string(),
  role: z.string(),
  profile: z.object({
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string(),
    position: z.string(),
    avatarId: z.number().nullable(),
  }),
  companyId: z.number().optional(),
});

export const $GetUsersWithPaginationResponse =
  $getPaginationResponseType($User);
export type GetUsersWithPaginationResponse = z.infer<
  typeof $GetUsersWithPaginationResponse
>;

export const $CreateUserRequest = z.object({
  email: z.string(),
  role: z.string(),
  profile: z.object({
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string(),
  }),
  companyId: z.number().optional(),
  password: z.string(),
  password_confirmation: z.string(),
});
export type CreateUserRequest = z.infer<typeof $CreateUserRequest>;

export type DeleteUserRequest = z.infer<typeof $DeleteUserRequest>;
export const $DeleteUserRequest = z.object({
  ids: z.array(z.number()),
});

export type ChangeActivityRequest = z.infer<typeof $ChangeActivityRequest>;
export const $ChangeActivityRequest = z.object({
  isActive: z.boolean(),
});

export const $ChangeActivityResponse = z.object({
  isActive: z.boolean(),
});
export type ChangeActivityResponse = z.infer<typeof $ChangeActivityResponse>;

const $UserAiMessageFileType = z.object({
  value: z.string(),
  name: z.string(),
});

const $UserAiMessageFile = z.object({
  id: z.number(),
  name: z.string(),
  extension: z.string(),
  size: z.number(),
  absolutePath: z.string(),
  type: $UserAiMessageFileType.optional(),
  scope: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const USER_AI_MESSAGE_VACANCY_ACTION_NAMES = [
  'vacancy_created',
  'vacancy_edited',
] as const;

export type UserAiMessageActionName =
  (typeof USER_AI_MESSAGE_ACTION_NAMES)[number];

export const USER_AI_MESSAGE_ACTION_NAMES = [
  ...USER_AI_MESSAGE_VACANCY_ACTION_NAMES,
  'swipe_candidates',
  'show_candidate',
  'show_vacancy',
  'single_vacancy_limit',
] as const;

export type UserAiMessageActionVacancy = z.infer<
  typeof $UserAiMessageActionVacancy
>;
export const $UserAiMessageActionVacancy = z.object({
  id: z.number().nullish(),
  url: z.string().nullish(),
  name: z.string().nullish(),
});

const $UserAiMessageInteractiveElement = z.object({
  type: z.string(),
  text: z.string().nullish(),
  label: z.string().nullish(),
  action: z
    .object({
      type: z.string(),
      vacancyId: z.number().nullish(),
      candidateVacancyId: z.number().nullish(),
      url: z.string().nullish(),
    })
    .nullish(),
});

export type UserAiMessageAction = z.infer<typeof $UserAiMessageAction>;
export const $UserAiMessageAction = z.object({
  id: z.number().nullish(),
  name: z.enum(USER_AI_MESSAGE_ACTION_NAMES).nullish(),
  template: z.string().nullish(),
  interactiveElements: z.array($UserAiMessageInteractiveElement).nullish(),
  data: z
    .object({
      vacancy: $UserAiMessageActionVacancy.nullish(),
      candidateVacancyId: z.number().nullish(),
    })
    .nullish(),
});

export type UserAiMessageSender = z.infer<typeof $UserAiMessageSender>;
export const $UserAiMessageSender = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
});

export type UserAiMessage = z.infer<typeof $UserAiMessage>;
export const $UserAiMessage = z.object({
  id: z.number(),
  userId: z.number(),
  contentType: z.enum(['Text', 'Audio']),
  content: z.string().nullable(),
  status: z.enum(['Success', 'Error', 'Pending']),
  transcription: z.string().nullable(),
  authorType: z.enum(['User', 'AI', 'Admin']),
  sender: $UserAiMessageSender.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  file: $UserAiMessageFile.nullable().optional(),
  actions: $UserAiMessageAction.nullable().optional(),
});

export const $ListUserAiMessagesRequest = $getFiltersRequestType(
  z.object({
    afterId: z.number().optional(),
  })
);
export type ListUserAiMessagesRequest = z.infer<
  typeof $ListUserAiMessagesRequest
>;

export type ListUserAiMessagesResponse = z.infer<
  typeof $ListUserAiMessagesResponse
>;
export const $ListUserAiMessagesResponse =
  $getPaginationResponseType($UserAiMessage);
